import type { ProfasePeriodoActividadData } from './profase-periodo-actividad';
import { openSuperContaChat } from '../commons/superconta-chat';
import {
  TIPO_PERIODO_LABEL,
  TIPO_PERIODO_VALUES,
  normalizeWorkflowPeriodicidad,
  type TipoPeriodo,
} from './workflow-periodicidad';

/** DTO de GET/PUT asignación workflow ↔ periodo actividad. Ver doc/ProfaseWorkflowPeriodo.MD */
export interface PeriodoActividadWorkflowPeriodo {
  id: number;
  nombre?: string | null;
  tipoPeriodo?: string | null;
  numeroPeriodo?: number | null;
  data?: ProfasePeriodoActividadData | null;
  generarAutomaticamente?: boolean | null;
  habilitado: boolean;
  asignado: boolean;
  workflowPeriodoId?: number | null;
  vigente?: boolean | null;
  vigenteDesde?: string | null;
  vigenteHasta?: string | null;
}

export function parseConfigPeriodos(raw: unknown): PeriodoActividadWorkflowPeriodo[] {
  if (!Array.isArray(raw)) return [];
  const result: PeriodoActividadWorkflowPeriodo[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Partial<PeriodoActividadWorkflowPeriodo> & { id?: unknown };
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    result.push({
      id,
      nombre: row.nombre ?? null,
      tipoPeriodo: row.tipoPeriodo ?? null,
      numeroPeriodo: row.numeroPeriodo ?? null,
      data: row.data ?? null,
      generarAutomaticamente: row.generarAutomaticamente ?? null,
      habilitado: row.habilitado !== false,
      asignado: row.asignado !== false,
      workflowPeriodoId: row.workflowPeriodoId ?? null,
      vigente: row.vigente ?? null,
      vigenteDesde: row.vigenteDesde ?? null,
      vigenteHasta: row.vigenteHasta ?? null,
    });
  }
  return result;
}

export function assignedPeriodosFromSelection(
  items: PeriodoActividadWorkflowPeriodo[],
  selectedIds: Iterable<number>,
): PeriodoActividadWorkflowPeriodo[] {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const fromItems = items
    .filter(item => ids.has(item.id))
    .map(item => ({ ...item, asignado: true }));
  const found = new Set(fromItems.map(item => item.id));
  const extras: PeriodoActividadWorkflowPeriodo[] = Array.from(ids)
    .filter(id => !found.has(id))
    .map(id => ({ id, habilitado: true, asignado: true }));
  return [...fromItems, ...extras];
}

export function serializePeriodosForConfigSave(
  periodos: PeriodoActividadWorkflowPeriodo[],
): Array<{ id: number }> {
  return periodos.map(periodo => ({ id: periodo.id }));
}

export const PERIODO_SELECCIONAR_EVENT = 'profase:periodo-seleccionar';

export function canonicalTipoPeriodo(raw: unknown): string {
  const trimmed = raw == null ? '' : String(raw).trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  for (const code of TIPO_PERIODO_VALUES) {
    if (code.toLowerCase() === lower || TIPO_PERIODO_LABEL[code].toLowerCase() === lower) {
      return code;
    }
  }
  const normalized = normalizeWorkflowPeriodicidad(trimmed);
  if (TIPO_PERIODO_VALUES.includes(normalized as TipoPeriodo)) return normalized;
  return trimmed.toUpperCase();
}

export function parsePeriodoNumero(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
}

export function periodoMatchesTipoNumero(
  item: Pick<PeriodoActividadWorkflowPeriodo, 'tipoPeriodo' | 'numeroPeriodo'>,
  tipo: unknown,
  numero: unknown,
): boolean {
  const wantTipo = canonicalTipoPeriodo(tipo);
  if (!wantTipo || canonicalTipoPeriodo(item.tipoPeriodo) !== wantTipo) return false;
  const wantNumero = parsePeriodoNumero(numero);
  const gotNumero = item.numeroPeriodo == null ? null : Number(item.numeroPeriodo);
  if (wantNumero == null) return gotNumero == null;
  return gotNumero === wantNumero;
}

export function matchingHabilitadoPeriodoIds(
  items: PeriodoActividadWorkflowPeriodo[],
  tipo: unknown,
  numero: unknown,
): number[] {
  return items
    .filter(item => item.habilitado && periodoMatchesTipoNumero(item, tipo, numero))
    .map(item => item.id);
}

export function findPeriodoSeleccionarMatch(
  items: PeriodoActividadWorkflowPeriodo[],
  tipo: unknown,
  numero: unknown,
): PeriodoActividadWorkflowPeriodo | null {
  return items.find(item => item.habilitado && periodoMatchesTipoNumero(item, tipo, numero)) ?? null;
}

export type PeriodoSeleccionarFailPayload = { message: string };
export type PeriodoSeleccionarCallback = (obj?: PeriodoSeleccionarFailPayload) => void;

export type PeriodoSeleccionarRequest = {
  tipo: unknown;
  numero: unknown;
  onSuccess?: PeriodoSeleccionarCallback;
  onFail?: PeriodoSeleccionarCallback;
};

export const PERIODO_SELECCIONAR_NO_MATCH_MESSAGE =
  'No se encontró ni un periodo para ser asignado';

export function asPeriodoSeleccionarCallback(value: unknown): PeriodoSeleccionarCallback | undefined {
  return typeof value === 'function' ? (value as PeriodoSeleccionarCallback) : undefined;
}

export function periodoSeleccionarFailPayload(): PeriodoSeleccionarFailPayload {
  return { message: PERIODO_SELECCIONAR_NO_MATCH_MESSAGE };
}

export function runOptionalPeriodoSeleccionarCallback(
  callback?: PeriodoSeleccionarCallback,
  obj?: PeriodoSeleccionarFailPayload,
): void {
  if (!callback) return;
  try {
    callback(obj);
  } catch {
    /* Super Conta callbacks must not break the modal */
  }
}

export function periodoSeleccionar(
  tipo: unknown,
  numero: unknown,
  onSuccess?: unknown,
  onFail?: unknown,
): void {
  const detail: PeriodoSeleccionarRequest = { tipo, numero };
  const success = asPeriodoSeleccionarCallback(onSuccess);
  const fail = asPeriodoSeleccionarCallback(onFail);
  if (success) detail.onSuccess = success;
  if (fail) detail.onFail = fail;
  window.dispatchEvent(new CustomEvent(PERIODO_SELECCIONAR_EVENT, { detail }));
}

if (typeof window !== 'undefined') {
  window.periodoSeleccionar = periodoSeleccionar;
}

export const PERIODOS_CONFIG_WARNING_HELP_LINK = 'Click aquí';
export const PERIODOS_CONFIG_WARNING_HELP = `${PERIODOS_CONFIG_WARNING_HELP_LINK} si necesita ayuda ?`;
export const PERIODOS_EMPTY_WARNING_CHAT_MESSAGE =
  'Tengo una advertencia de que mi workflow no tiene ni un periodo asignado';
export const PERIODOS_MULTI_WARNING_CHAT_MESSAGE =
  'Tengo una advertencia de mas de un periodo para un workflow, que significa eso';

export function periodosConfigWarningChatPrompt(count: number): string | null {
  if (count <= 0) return PERIODOS_EMPTY_WARNING_CHAT_MESSAGE;
  if (count > 1) return PERIODOS_MULTI_WARNING_CHAT_MESSAGE;
  return null;
}

export function openPeriodosConfigWarningHelp(count: number): void {
  const prompt = periodosConfigWarningChatPrompt(count);
  if (prompt) {
    window.addMessageToChat(prompt);
    return;
  }
  openSuperContaChat();
}

/** Exactamente un periodo asignado es válido; 0 o más de 1 requieren aviso. */
export function periodosConfigWarningMessage(count: number): string | null {
  if (count <= 0) {
    return 'No tiene ni un periodo asignado para este Workflow';
  }
  if (count > 1) {
    return 'Tiene mas de un periodo para este Workflow';
  }
  return null;
}

export function periodosConfigWarningTitle(count: number): string | null {
  const message = periodosConfigWarningMessage(count);
  return message ? `${message}. ${PERIODOS_CONFIG_WARNING_HELP}` : null;
}
